import { type ReactNode } from 'react';

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-bold text-slate-900">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function StatCard({ label, value, hint, tone = 'default' }: { label: string; value: ReactNode; hint?: string; tone?: 'default' | 'good' | 'warn' | 'bad' }) {
  const toneClass =
    tone === 'good' ? 'text-seagreen-600' : tone === 'warn' ? 'text-amber-600' : tone === 'bad' ? 'text-rose-600' : 'text-slate-900';
  return (
    <div className="card px-4 py-3.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 text-lg font-bold tabular-nums ${toneClass}`}>{value}</div>
      {hint ? <div className="mt-0.5 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

// One entry per status string any m30 endpoint actually returns — see
// src/lib/types.ts for the full enum inventory this was built against.
const STATUS_TONES: Record<string, string> = {
  // Generic good/open/closed buckets shared across several enums
  ACTIVE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  RECOGNIZED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  APPROVED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  CONFIRMED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  PAID: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  COMPLETED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  PUBLISHED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  NO_OBJECTION: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  RESPONDED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',

  DRAFT: 'bg-slate-100 text-slate-600 ring-slate-200',
  INACTIVE: 'bg-slate-100 text-slate-600 ring-slate-200',

  PENDING: 'bg-sky-50 text-sky-700 ring-sky-200',
  PENDING_PAYMENT: 'bg-sky-50 text-sky-700 ring-sky-200',
  PAY_PENDING: 'bg-sky-50 text-sky-700 ring-sky-200',
  SUBMITTED: 'bg-sky-50 text-sky-700 ring-sky-200',
  REQUESTED: 'bg-sky-50 text-sky-700 ring-sky-200',
  QUEUED: 'bg-sky-50 text-sky-700 ring-sky-200',

  UNDER_REVIEW: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  IN_PROGRESS: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  REFERRALS_PENDING: 'bg-indigo-50 text-indigo-700 ring-indigo-200',

  SCORED: 'bg-violet-50 text-violet-700 ring-violet-200',
  DISBURSING: 'bg-violet-50 text-violet-700 ring-violet-200',

  PROBATIONARY: 'bg-amber-50 text-amber-700 ring-amber-200',
  CONDITIONAL: 'bg-amber-50 text-amber-700 ring-amber-200',
  QUERIED: 'bg-amber-50 text-amber-700 ring-amber-200',
  SUSPENDED: 'bg-amber-50 text-amber-700 ring-amber-200',
  WARN: 'bg-amber-50 text-amber-700 ring-amber-200',

  REJECTED: 'bg-rose-50 text-rose-700 ring-rose-200',
  DECLINED: 'bg-rose-50 text-rose-700 ring-rose-200',
  OBJECTION: 'bg-rose-50 text-rose-700 ring-rose-200',
  FAILED: 'bg-rose-50 text-rose-700 ring-rose-200',
  RESTRICTED: 'bg-rose-50 text-rose-700 ring-rose-200',
  DARK: 'bg-slate-800 text-slate-100 ring-slate-700',

  CANCELLED: 'bg-slate-100 text-slate-500 ring-slate-200',
  WITHDRAWN: 'bg-slate-100 text-slate-500 ring-slate-200',
  CLOSED: 'bg-slate-100 text-slate-500 ring-slate-200',
  ARCHIVED: 'bg-slate-100 text-slate-500 ring-slate-200',
  EXPIRED: 'bg-slate-100 text-slate-500 ring-slate-200',
  DEEMED_NO_OBJECTION: 'bg-slate-100 text-slate-500 ring-slate-200',

  PUBLIC: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
};

export function StatusBadge({ status }: { status: string }) {
  const tone = STATUS_TONES[status] ?? 'bg-slate-100 text-slate-600 ring-slate-200';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${tone}`}>
      {status.replaceAll('_', ' ')}
    </span>
  );
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-8 text-sm text-slate-500">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-brand-600" />
      {label}
    </div>
  );
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">{message}</div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="px-4 py-10 text-center">
      <div className="text-sm font-semibold text-slate-600">{title}</div>
      {hint ? <div className="mt-1 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-950/50 p-4 pt-[8vh]" onClick={onClose}>
      <div
        className={`card w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} p-5`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          <button className="rounded-md px-2 py-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children, className = '' }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

/** Simple pill tab bar shared by every page in this app that splits one module role's work into sub-views. */
export function Tabs<T extends string>({ tabs, active, onChange }: { tabs: ReadonlyArray<{ key: T; label: string }>; active: T; onChange: (key: T) => void }) {
  return (
    <div className="mb-4 flex flex-wrap gap-1 rounded-xl bg-slate-200/60 p-1">
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
            active === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function SectionCard({ title, actions, children }: { title?: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <div className="card p-5">
      {title || actions ? (
        <div className="mb-3 flex items-center justify-between">
          {title ? <h2 className="text-sm font-bold text-slate-700">{title}</h2> : <span />}
          {actions}
        </div>
      ) : null}
      {children}
    </div>
  );
}
